import { useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, LockKeyhole, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FileRenameDialog = ({ file, onClose, onRenamed }) => {
  const dot = file.filename.lastIndexOf('.');
  const extension = dot > 0 ? file.filename.slice(dot) : '';
  const [value, setValue] = useState(extension ? file.filename.slice(0, dot) : file.filename);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const savingRef = useRef(false);
  let stem = value.trim();
  if (extension && stem.toLowerCase().endsWith(extension.toLowerCase())) stem = stem.slice(0, -extension.length).trim();
  const filename = stem + extension;
  const invalid = !stem.replace(/[.\s]/g, '') ? 'Enter a file name.'
    : /[<>:"/\\|?*\x00-\x1f\x7f]/.test(stem) ? 'Remove special characters: < > : " / \\ | ? *.'
    : filename.length > 200 || new TextEncoder().encode(filename).length > 255 ? 'This name is too long. Please shorten it.' : '';

  const submit = async (event) => {
    event.preventDefault();
    if (savingRef.current) return;
    if (invalid) { setError(invalid); return; }
    if (filename === file.filename) { onClose(); return; }
    savingRef.current = true; setSaving(true); setError('');
    try {
      const password = sessionStorage.getItem('admin_password');
      const { data } = await axios.patch(`${API}/admin/library/files/${file.id}`, { filename }, {
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
        timeout: 20000,
      });
      onRenamed(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not rename this file. Please try again.');
    } finally { savingRef.current = false; setSaving(false); }
  };

  return <Dialog open onOpenChange={(open) => { if (!open && !savingRef.current) onClose(); }}>
    <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-lg" data-testid="file-rename-dialog"
      closeButtonProps={{ disabled: saving, 'data-testid': 'file-rename-close' }}
      onOpenAutoFocus={(event) => { event.preventDefault(); inputRef.current?.select(); }}>
      <DialogHeader>
        <DialogTitle>Rename file</DialogTitle>
        <DialogDescription className="break-all pr-4" data-testid="file-rename-original">{file.filename}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="min-w-0 space-y-4" data-testid="file-rename-form">
        <div>
          <Label htmlFor="file-rename-name">File name</Label>
          <div className="mt-2 flex min-w-0 items-stretch gap-2">
            <Input id="file-rename-name" ref={inputRef} value={value} maxLength={200} disabled={saving}
              onChange={(event) => { setValue(event.target.value); setError(''); }}
              aria-invalid={!!error} aria-describedby={error ? 'file-rename-error' : undefined}
              className="min-w-0 flex-1" data-testid="file-rename-input" />
            {extension && <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-gray-50 px-3 text-sm text-gray-600" data-testid="file-rename-extension" title="File extension is protected"><LockKeyhole className="h-3.5 w-3.5" />{extension}</span>}
          </div>
          <p className="mt-2 break-all text-xs text-gray-500" data-testid="file-rename-result">{filename}</p>
        </div>
        {error && <p id="file-rename-error" role="alert" className="text-sm text-red-700" data-testid="file-rename-error">{error}</p>}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={saving} onClick={onClose} data-testid="file-rename-cancel">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="file-rename-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
};